import React from 'react';
import PropTypes from 'prop-types';
import { every, each } from 'lodash';

import { LocaleService } from '@sm360/widget-commons-core-services';
import {
    ErrorsHandler,
    FIELD_LENGTH,
    GroupResponseService,
    InputDropDown,
    InputText,
} from '@sm360/widget-commons-forms';

import { EMPLOYMENT_TYPES, EMPLOYMENT_STATUS, FINANCING_FREQUENCIES } from 'financing/constants/employment.js';
import { EXTERNAL_INCOMES } from 'financing/constants/incomes.js';

import employmentInfosConfig from 'financing/components/EmploymentInfosForm/EmploymentInfosConfig.js';
import ErrorService from 'financing/services/ErrorService/ErrorService.js';

import FormBuilder from 'financing/components/Utils/FormBuilder.jsx';
import ButtonBlock from 'financing/components/Utils/ButtonBlock.jsx';

class EmploymentInfosForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isSubmitted: false,
            isValid: false,
            employmentType: '',
            employmentStatus: '',
            employmentInfos: {},
            withOtherIncome: false,
            otherIncomeType: '',
            otherIncomeFrequency: '',
            otherIncomeAmount: 0,
            formValidation: {
                employmentType: false,
                employmentStatus: false,
            },
            otherIncomeValidation: {
                otherIncomeType: '',
                otherIncomeFrequency: '',
                otherIncomeAmount: 0,
            },
            employmentInfosValidation: {},
            errors: [],
        };

        this.onEmploymentValueChange = this.onEmploymentValueChange.bind(this);
        this.onEmploymentTypeChange = this.onEmploymentTypeChange.bind(this);
        this.onEmploymentStatusChange = this.onEmploymentStatusChange.bind(this);
        this.onOtherIncomeStatusChanged = this.onOtherIncomeStatusChanged.bind(this);
        this.onOtherIncomeTypeChanged = this.onOtherIncomeTypeChanged.bind(this);
        this.onOtherIncomeFrequencyChanged = this.onOtherIncomeFrequencyChanged.bind(this);
        this.onOtherIncomeAmountChanged = this.onOtherIncomeAmountChanged.bind(this);
        this.onOtherIncomeStatusChanged = this.onOtherIncomeStatusChanged.bind(this);
        this.onDone = this.onDone.bind(this);
        this.onBack = this.onBack.bind(this);
    }

    componentDidMount() {
        this.employmentTypes = EMPLOYMENT_TYPES.map((type) => ({
            ...type,
            label: LocaleService.t(type.transKey),
        }));

        this.employmentStatuses = EMPLOYMENT_STATUS.map((status) => ({
            ...status,
            label: LocaleService.t(status.transKey),
        }));

        this.externalIncomes = EXTERNAL_INCOMES.map((income) => ({
            ...income,
            label: LocaleService.t(income.transKey),
        }));

        this.financingFrequencies = FINANCING_FREQUENCIES.map((frequency) => ({
            ...frequency,
            label: LocaleService.t(frequency.transKey),
        }));
    }

    onDone(callbackObject) {
        const { withOtherIncome, formValidation, otherIncomeValidation, employmentInfosValidation } = this.state;

        const errors = ErrorService.getErrors(formValidation, 'FINANCING.EMPLOYMENT_INFOS.ERRORS');

        if (!every(employmentInfosValidation)) {
            errors.push({
                code: 'error.employmentInfos',
                message: LocaleService.t('FINANCING.EMPLOYMENT_INFOS.ERRORS.EMPLOYMENT_INFOS'),
            });
        }

        if (withOtherIncome && !every(otherIncomeValidation)) {
            errors.push({
                code: 'error.otherIncome',
                message: LocaleService.t('FINANCING.EMPLOYMENT_INFOS.ERRORS.OTHER_INCOME'),
            });
        }

        this.setState({
            errors,
            isSubmitted: true,
            isValid: !errors.length,
        });

        this.props.onDone(
            GroupResponseService.createResponse(!errors.length, callbackObject, null, null, 'employmentInformations')
        );
    }

    onBack(callbackObject) {
        this.props.onBack(
            GroupResponseService.createResponse(true, callbackObject, null, null, 'employmentInformations')
        );
    }

    onEmploymentTypeChange(newValue) {
        if (!newValue) {
            return;
        }

        this.setState((oldState) => {
            const { formValidation } = oldState;

            const employmentInfosValidation = {};
            each(employmentInfosConfig[newValue.value], (input) => {
                employmentInfosValidation[input.inputName] = !input.isRequired;
            });

            formValidation.employmentType = true;

            return {
                formValidation,
                employmentInfosValidation,
                employmentType: newValue.value,
                employmentInfos: {},
            };
        });
    }

    onEmploymentStatusChange(newValue) {
        if (!newValue) {
            return;
        }

        this.setState((oldState) => {
            const { formValidation } = oldState;

            formValidation.employmentStatus = true;

            return {
                formValidation,
                employmentStatus: newValue.value,
            };
        });
    }

    onEmploymentValueChange(newValue, inputName, isValid) {
        this.setState((oldState) => {
            const { employmentInfos, employmentInfosValidation } = oldState;

            employmentInfos[inputName] = newValue;
            employmentInfosValidation[inputName] = isValid;

            return { employmentInfos, employmentInfosValidation };
        });
    }

    onOtherIncomeTypeChanged(newValue) {
        this.setState((oldState) => {
            const { otherIncomeValidation } = oldState;

            if (newValue && newValue !== '') {
                otherIncomeValidation.otherIncomeType = true;
            }

            return {
                otherIncomeValidation,
                otherIncomeType: newValue.value,
            };
        });
    }

    onOtherIncomeFrequencyChanged(newValue) {
        this.setState((oldState) => {
            const { otherIncomeValidation } = oldState;

            if (newValue && newValue !== '') {
                otherIncomeValidation.otherIncomeFrequency = true;
            }

            return {
                otherIncomeValidation,
                otherIncomeFrequency: newValue.value,
            };
        });
    }

    onOtherIncomeAmountChanged(newValue) {
        this.setState((oldState) => {
            const { otherIncomeValidation } = oldState;

            if (newValue && newValue !== '') {
                otherIncomeValidation.otherIncomeAmount = true;
            }

            return {
                otherIncomeValidation,
                otherIncomeAmount: newValue,
            };
        });
    }

    onOtherIncomeStatusChanged(event) {
        const newValue = event.currentTarget.value === 'yes';

        this.setState({
            withOtherIncome: newValue,
        });
    }

    render() {
        const { isOpen } = this.props;
        const {
            employmentInfos,
            employmentInfosValidation,
            employmentType,
            employmentStatus,
            formValidation,
            otherIncomeType,
            otherIncomeFrequency,
            otherIncomeAmount,
            otherIncomeValidation,
            withOtherIncome,
            isSubmitted,
            isValid,
            errors,
        } = this.state;

        let employmentTypeFormBlock = null;
        if (employmentType) {
            employmentTypeFormBlock = employmentInfosConfig[employmentType].map((input) => (
                <FormBuilder
                    key={input.inputName}
                    inputParams={{ ...input, value: employmentInfos[input.inputName] }}
                    isValid={employmentInfosValidation[input.inputName]}
                    onChange={this.onEmploymentValueChange}
                    hasBeenSubmit={isSubmitted}
                    errorLabel={input.errorTransKey}
                />
            ));
        }

        const employmentForm = (
            <div>
                <h4 className="sr-text is-bold">{LocaleService.t('FINANCING.EMPLOYMENT_INFOS.YOUR_WORK')}</h4>
                <br />
                <div className="clearfix">
                    <InputDropDown
                        name="employmentType"
                        classNames={['third']}
                        options={this.employmentTypes}
                        placeholder={LocaleService.t('FINANCING.EMPLOYMENT_INFOS.EMPLOYMENT_TYPE')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EMPLOYMENT_INFOS.EMPLOYMENT_TYPE')}
                        value={employmentType}
                        valueChange={this.onEmploymentTypeChange}
                        isRequired
                        isValid={formValidation.employmentType}
                        hasBeenSubmit={isSubmitted}
                    />

                    <InputDropDown
                        name="employmentStatus"
                        classNames={['third']}
                        options={this.employmentStatuses}
                        placeholder={LocaleService.t('FINANCING.EMPLOYMENT_INFOS.EMPLOYMENT_STATUS')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EMPLOYMENT_INFOS.EMPLOYMENT_STATUS')}
                        value={employmentStatus}
                        valueChange={this.onEmploymentStatusChange}
                        isRequired
                        isValid={formValidation.employmentStatus}
                        hasBeenSubmit={isSubmitted}
                    />
                </div>
                <div className="clearfix">{employmentTypeFormBlock}</div>
            </div>
        );

        const extendedOtherIncomeBlock = (
            <div className="clearfix">
                <InputDropDown
                    name="otherIncomeType"
                    classNames={['third']}
                    options={this.externalIncomes}
                    placeholder={LocaleService.t('FINANCING.EMPLOYMENT_INFOS.OTHER_INCOME')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EMPLOYMENT_INFOS.OTHER_INCOME')}
                    value={otherIncomeType}
                    valueChange={this.onOtherIncomeTypeChanged}
                    isValid={otherIncomeValidation.otherIncomeType}
                    hasBeenSubmit={isSubmitted}
                />
                <InputDropDown
                    name="otherIncomeFrequency"
                    classNames={['third']}
                    options={this.financingFrequencies}
                    value={otherIncomeFrequency}
                    valueChange={this.onOtherIncomeFrequencyChanged}
                    placeholder={LocaleService.t('FINANCING.EMPLOYMENT_INFOS.OTHER_INCOME_FREQUENCY')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EMPLOYMENT_INFOS.OTHER_INCOME_FREQUENCY')}
                    isValid={otherIncomeValidation.otherIncomeFrequency}
                    hasBeenSubmit={isSubmitted}
                />
                <InputText
                    type="number"
                    name="otherIncomeAmount"
                    classNames={['styled-placeholder', 'third', 'currency']}
                    label={LocaleService.t('FINANCING.EMPLOYMENT_INFOS.INCOME_AMOUNT')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EMPLOYMENT_INFOS.INCOME_AMOUNT')}
                    value={otherIncomeAmount}
                    valueChange={this.onOtherIncomeAmountChanged}
                    isValid={otherIncomeValidation.otherIncomeAmount}
                    hasBeenSubmit={isSubmitted}
                    maxLength={FIELD_LENGTH.DEFAULT_NUMBER_MAX}
                />
            </div>
        );

        const otherIncomeForm = (
            <div className="clearfix">
                <h4 className="sr-text is-bold">{LocaleService.t('FINANCING.EMPLOYMENT_INFOS.OTHER_INCOME')}</h4>
                <br />
                <div className="clearfix">
                    <div className="styled-placeholder third">
                        <div className="radio-button-cta-wrapper">
                            <div className="radio-button-cta-wrapper--single">
                                <input
                                    type="radio"
                                    id="yesOption"
                                    name="hasOtherIncome"
                                    value="yes"
                                    onChange={this.onOtherIncomeStatusChanged}
                                    checked={this.state.withOtherIncome}
                                />
                                <label htmlFor="yesOption">
                                    {LocaleService.t('FINANCING.EMPLOYMENT_INFOS.YES_OPTION')}
                                </label>
                            </div>
                            <div className="radio-button-cta-wrapper--single">
                                <input
                                    type="radio"
                                    id="noOption"
                                    name="hasOtherIncome"
                                    value="no"
                                    onChange={this.onOtherIncomeStatusChanged}
                                    checked={!this.state.withOtherIncome}
                                />
                                <label htmlFor="noOption">
                                    {LocaleService.t('FINANCING.EMPLOYMENT_INFOS.NO_OPTION')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                {withOtherIncome && extendedOtherIncomeBlock}
            </div>
        );

        const formClass = isOpen
            ? 'financingBlockForm employmentForm is-open'
            : `financingBlockForm employmentForm is-closed ${isValid ? 'is-valid' : ''}`;

        const callbackObject = {
            type: employmentType,
            status: employmentStatus,
            otherIncome: {
                type: otherIncomeType,
                frequency: otherIncomeFrequency,
                amount: otherIncomeAmount && otherIncomeAmount,
            },
            ...employmentInfos,
        };

        return (
            <div className={formClass}>
                <icon className="icon-Crochet is-util" />
                {errors.length !== 0 && isSubmitted && isOpen && <ErrorsHandler errors={errors} />}
                <h3 className="sr-text is-bold is-util ">
                    {LocaleService.t('FINANCING.EMPLOYMENT_INFOS.WHAT_ABOUT_CURRENT_EMPLOYMENT')}
                </h3>
                <br />
                {isOpen && employmentForm}
                {isOpen && otherIncomeForm}
                {errors.length !== 0 &&
                    isSubmitted &&
                    isOpen && (
                        <p className="financing__content-errors-message">
                            {LocaleService.t('COMMONS_FORMS.ERRORS.TITLE')}
                        </p>
                    )}
                {isOpen && (
                    <ButtonBlock
                        onBack={() => this.onBack(callbackObject)}
                        onDone={() => this.onDone(callbackObject)}
                    />
                )}
            </div>
        );
    }
}

EmploymentInfosForm.defaultProps = {
    isOpen: true,
};

EmploymentInfosForm.propTypes = {
    isOpen: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
    onDone: PropTypes.func.isRequired,
};

EmploymentInfosForm.displayName = 'EmploymentInfosForm';

export default EmploymentInfosForm;
